import type { Analysis, DetailDraft, Listing } from '@/features/dashboard/types';
import { localeToSupportedLanguageCode, normalizeToSupportedLanguageCode, toAppLocale } from '@/lib/i18n';
import { normalizeQuantity } from '@/lib/lot-pricing';

export type ListingActionKind = 'sell' | 'donate' | 'recycle' | 'repair' | '';

// Action kinds that take the item out of resale: their headline is the action, not a price.
const NON_SALE_ACTION_KINDS = new Set<ListingActionKind>(['donate', 'recycle', 'repair']);

export function getListingActionKind(listing: Listing | null | undefined): ListingActionKind {
  return listing?.current_action_summary?.primary_kind ?? '';
}

// Whether a listing contributes to the cumulative dashboard value. Only sold and to-sell
// items count; donation/recycling/repair carry no resale value. Not-yet-routed items (no
// action chosen) keep counting — their headline value is still a resale estimate.
export function listingCountsTowardTotalValue(listing: Listing | null | undefined): boolean {
  return !NON_SALE_ACTION_KINDS.has(getListingActionKind(listing));
}

const UNKNOWN_BRAND_MARKERS = new Set([
  'unknown',
  'not known',
  'not available',
  'none',
  'null',
  'n/a',
  'na',
  'desconocido',
  'desconocida',
  'disconocido',
  'disconocida',
  'inconnu',
  'inconnue',
  'unbekannt',
  'sconosciuto',
  'sconosciuta',
  'sin marca',
  'marca desconocida',
  'marca no conocida',
  'no brand',
  'unknown brand',
]);

function normalizeUnknownBrand(value: string): string {
  const cleaned = value.trim().toLowerCase();
  if (!cleaned) {
    return '';
  }

  const normalized = cleaned.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }

  if (UNKNOWN_BRAND_MARKERS.has(normalized)) {
    return '';
  }
  if (normalized.startsWith('unknown ')) {
    return '';
  }
  if (normalized.endsWith(' unknown')) {
    return '';
  }
  if (normalized.includes(' brand unknown ') || normalized === 'brand unknown') {
    return '';
  }
  if (normalized.includes(' no brand ') || normalized === 'no brand') {
    return '';
  }
  if (normalized.includes('sin marca')) {
    return '';
  }
  if (normalized.includes('marca desconoc') || normalized.includes('disconocid')) {
    return '';
  }
  if (normalized.includes('inconnu')) {
    return '';
  }
  if (normalized.includes('unbekannt')) {
    return '';
  }
  if (normalized.includes('sconosciut')) {
    return '';
  }

  return value.trim();
}

type TitleCarrier = {
  title?: string | null;
  localized_title?: string | null;
  localized_locale?: string | null;
};

// Uppercase the first letter for display, while preserving intentional brand casing
// (iPad, iPhone, eBay…): if the first char is already non-lowercase, or the second
// char is uppercase (camelCase brand), leave the string untouched.
export function capitalizeTitleForDisplay(value: string): string {
  if (!value) {
    return value;
  }
  const first = value.charAt(0);
  if (first === first.toUpperCase()) {
    return value;
  }
  const second = value.charAt(1);
  if (second && second === second.toUpperCase() && second !== second.toLowerCase()) {
    return value;
  }
  return `${first.toUpperCase()}${value.slice(1)}`;
}

// Raw (stored-case) display title. Use this when the value feeds an editable field or
// an equality check against stored data, so we don't register a spurious user override.
export function resolveDisplayTitle(item: TitleCarrier | null | undefined, activeLanguage: string): string {
  const canonicalTitle = String(item?.title || '').trim();
  const localizedTitle = String(item?.localized_title || '').trim();
  const localizedLocale = String(item?.localized_locale || '').trim();
  if (!localizedTitle || !localizedLocale) {
    return canonicalTitle;
  }

  const activeLanguageCode = normalizeToSupportedLanguageCode(activeLanguage);
  const localizedLanguageCode = localeToSupportedLanguageCode(localizedLocale);
  if (activeLanguageCode !== localizedLanguageCode) {
    return canonicalTitle;
  }

  return localizedTitle;
}

// Display title with a capitalized first letter — used everywhere a title is shown.
export function getDisplayTitle(item: TitleCarrier | null | undefined, activeLanguage: string): string {
  return capitalizeTitleForDisplay(resolveDisplayTitle(item, activeLanguage));
}

export function formatCategoryLabel(value: string, translatedLabels?: Record<string, string>): string {
  const normalizedValue = value.trim();
  const translationKey = normalizedValue.toLowerCase().replace(/\s+/g, '-');
  if (translatedLabels?.[translationKey]) {
    return translatedLabels[translationKey];
  }

  const normalized = normalizedValue.replace(/[_-]+/g, ' ');
  if (!normalized) {
    return translatedLabels?.other || 'Other';
  }

  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatConditionLabel(value: string, translatedConditions?: Record<string, string>): string {
  const normalizedKey = value.trim().toLowerCase().replace(/-/g, '_');
  if (translatedConditions?.[normalizedKey]) {
    return translatedConditions[normalizedKey];
  }
  return formatCategoryLabel(value);
}

export function defaultDraft(currency: string): DetailDraft {
  return {
    title: '',
    conditionGrade: 'good',
    functionalState: 'working',
    estimatedMin: '',
    estimatedMax: '',
    currency,
    purchaseYear: '',
    purchasePrice: '',
    brand: '',
    model: '',
    sizeLabel: '',
    quantity: '1',
    domain: 'other',
    objectType: 'general-item',
    subtype: '',
    notes: '',
  };
}

export function toNumber(input: string): number | null {
  const normalized = input.replace(',', '.').trim();
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeCurrency(currency: string): string {
  void currency;
  return 'EUR';
}

function getCurrencyLocale(currency: string): string {
  void currency;
  return 'fr-FR';
}

function isTrailingCurrency(currency: string): boolean {
  return normalizeCurrency(currency) === 'EUR';
}

export function formatMoney(value: number, currency: string): string {
  const normalizedCurrency = normalizeCurrency(currency);
  const locale = getCurrencyLocale(normalizedCurrency);

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    const amount = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(value);
    const symbol = getCurrencySymbol(normalizedCurrency);
    return isTrailingCurrency(normalizedCurrency) ? `${amount} ${symbol}` : `${symbol}${amount}`;
  }
}

export function formatMoneyRange(minValue: number, maxValue: number, currency: string): string {
  const normalizedCurrency = normalizeCurrency(currency);
  const locale = getCurrencyLocale(normalizedCurrency);

  try {
    const amountFormatter = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    });
    const amountRange = `${amountFormatter.format(minValue)}–${amountFormatter.format(maxValue)}`;
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).formatToParts(1);
    const currencyIndex = parts.findIndex((part) => part.type === 'currency');
    const integerIndex = parts.findIndex((part) => part.type === 'integer');

    return currencyIndex !== -1 && integerIndex !== -1 && currencyIndex < integerIndex
      ? `${getCurrencySymbol(normalizedCurrency)}${amountRange}`
      : `${amountRange} ${getCurrencySymbol(normalizedCurrency)}`;
  } catch {
    const amountFormatter = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    });
    const amountRange = `${amountFormatter.format(minValue)}–${amountFormatter.format(maxValue)}`;
    return isTrailingCurrency(normalizedCurrency)
      ? `${amountRange} ${getCurrencySymbol(normalizedCurrency)}`
      : `${getCurrencySymbol(normalizedCurrency)}${amountRange}`;
  }
}

// --- Estimations : arrondi d'affichage ------------------------------------
//
// Une estimation n'est pas un prix : « entre 29 et 37 € » affiche une précision
// que le calcul n'a pas, et sonne faux. On arrondit donc au 5 € le plus proche
// (décision David 23/07/2026).
//
// Exception sous 10 € : au 5 € près, un bavoir à 2-3 € tomberait à « 0-5 € ».
// En dessous de ce seuil on arrondit à l'euro, ce qui reste lisible et honnête.
//
// À réserver aux ESTIMATIONS. Les montants réels — offre reçue d'un acteur,
// vente confirmée, prix fixé par le vendeur dans sa boutique — passent par
// formatMoney et gardent leur valeur exacte : arrondir 37 € d'offre à 35 €
// serait un mensonge.

const ESTIMATE_ROUNDING_STEP = 5;
const ESTIMATE_ROUNDING_FLOOR = 10;

export function roundEstimate(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  if (value < ESTIMATE_ROUNDING_FLOOR) {
    return Math.round(value);
  }
  return Math.round(value / ESTIMATE_ROUNDING_STEP) * ESTIMATE_ROUNDING_STEP;
}

export function formatEstimate(value: number, currency: string): string {
  return formatMoney(roundEstimate(value), currency);
}

export function formatEstimateRange(minValue: number, maxValue: number, currency: string): string {
  const roundedMin = roundEstimate(minValue);
  const roundedMax = roundEstimate(maxValue);
  // Les deux bornes peuvent se rejoindre après arrondi (31-32 € → 30 €) :
  // afficher « 30-30 € » ferait bricolé.
  if (roundedMin === roundedMax) {
    return formatMoney(roundedMin, currency);
  }
  return formatMoneyRange(roundedMin, roundedMax, currency);
}

// Distance : UN SEUL formateur pour toute l'app, au format de la langue active.
// Les cinq écrans qui affichaient une distance utilisaient chacun leur toFixed(1),
// donc un point décimal même en français. La valeur, elle, vient toujours du serveur :
// jamais de calcul de distance côté client (David, 30/07/2026).
export function formatDistanceKm(
  value: number | null | undefined,
  language: string | null | undefined,
): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '';
  }
  const formatted = new Intl.NumberFormat(toAppLocale(language), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number(value));
  return `${formatted} km`;
}

export function formatMoneyPlaceholder(currency: string): string {
  const normalizedCurrency = normalizeCurrency(currency);
  const symbol = getCurrencySymbol(normalizedCurrency);
  return isTrailingCurrency(normalizedCurrency) ? `-- ${symbol}` : `${symbol} --`;
}

export function getCurrencySymbol(currency: string): string {
  const normalizedCurrency = normalizeCurrency(currency);
  const currencySymbols: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
  };
  return currencySymbols[normalizedCurrency] || normalizedCurrency;
}

export function composeDescription(draft: DetailDraft): string {
  return draft.notes.trim();
}

function parseLegacyDescription(description: string): { notes: string; purchaseYear: string; purchasePrice: string } {
  const purchaseYearPrefix = 'Purchase date:';
  const purchasePricePrefix = 'Initial cost:';
  let purchaseYear = '';
  let purchasePrice = '';
  const notes: string[] = [];

  for (const rawLine of description.split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }
    if (line.startsWith(purchaseYearPrefix)) {
      purchaseYear = line.slice(purchaseYearPrefix.length).trim();
      continue;
    }
    if (line.startsWith(purchasePricePrefix)) {
      purchasePrice = line.slice(purchasePricePrefix.length).trim();
      continue;
    }
    notes.push(rawLine);
  }

  return {
    notes: notes.join('\n').trim(),
    purchaseYear,
    purchasePrice,
  };
}

export function listingToDraft(listing: Listing, currency = 'EUR', activeLanguage = 'en'): DetailDraft {
  const legacyDescription = parseLegacyDescription(listing.description || '');
  const conditionGrade = listing.condition_grade === 'new' ? 'like_new' : listing.condition_grade;
  return {
    title: resolveDisplayTitle(listing, activeLanguage) || '',
    conditionGrade: conditionGrade || 'good',
    functionalState: listing.functional_state || 'working',
    estimatedMin: listing.estimated_price_min ? String(listing.estimated_price_min) : '',
    estimatedMax: listing.estimated_price_max ? String(listing.estimated_price_max) : '',
    currency: currency || 'EUR',
    purchaseYear: listing.purchase_year == null ? legacyDescription.purchaseYear : String(listing.purchase_year),
    purchasePrice: listing.purchase_price == null ? legacyDescription.purchasePrice : String(listing.purchase_price),
    brand: listing.brand ? normalizeUnknownBrand(listing.brand) : '',
    model: listing.model || '',
    sizeLabel: listing.size_label || '',
    quantity: String(normalizeQuantity(listing.quantity)),
    domain: listing.domain || 'other',
    objectType: listing.object_type || 'general-item',
    subtype: listing.subtype || '',
    // Description unique : ai_summary porte le texte servi partout ; les anciennes
    // notes utilisateur (listing.description) ne servent que de secours legacy.
    notes: (listing.ai_summary || '').trim() || legacyDescription.notes,
  };
}

export function analysisToDraft(analysis: Analysis, currency: string, activeLanguage = 'en'): DetailDraft {
  const draft = defaultDraft(analysis.estimated_currency || currency);
  const conditionGrade = analysis.condition_grade === 'new' ? 'like_new' : analysis.condition_grade;

  draft.title = resolveDisplayTitle(analysis, activeLanguage) || '';
  draft.conditionGrade = conditionGrade || 'good';
  draft.functionalState = analysis.functional_state || 'working';
  draft.estimatedMin = String(analysis.estimated_price_min || '');
  draft.estimatedMax = String(analysis.estimated_price_max || '');
  draft.brand = analysis.brand ? normalizeUnknownBrand(analysis.brand) : '';
  draft.model = analysis.model || '';
  // Taille lue sur l'étiquette par le modèle (prompt du 22/09/2026) ; l'utilisateur peut la corriger.
  draft.sizeLabel = (analysis.size_label || '').trim();
  draft.domain = analysis.domain || 'other';
  draft.objectType = analysis.object_type || 'general-item';
  draft.subtype = analysis.subtype || '';

  // Description unique (décision 21/07) : la case est préremplie avec le résumé IA
  // et devient LE texte de l'objet (fiche, boutique publique, e-mails d'offre).
  draft.notes = (analysis.ai_summary || '').trim();

  return draft;
}
